import React, { useState, useEffect } from 'react'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  CheckboxGroup,
  VStack,
  HStack,
  Checkbox,
  InputGroup,
  InputLeftElement,
  Input,
  Box,
  Text,
  useDisclosure,
  useBreakpointValue,
  useColorModeValue,
  Button,
  Icon,
} from '@chakra-ui/react'
import { FiltersIcon } from '@/components/icons/FiltersIcon'
import { DateRangePicker } from '@/components/DateRangePicker'
import {
  ORDER_TYPE_FILTER_CONFIG,
  PAYMENT_TYPE_FILTER_CONFIG,
  TRANSACTION_FILTER_CONFIG,
  UNPAID_INVOICE_FILTER_CONFIG,
} from '@/constants'
import {
  CreditFilterDepositIdTypes,
  HardwareInvoiceTypes,
  InvoiceTypes,
  ITransactionFilters,
  OrderFilterTypes,
} from '@/types/billing.interface'
import { OrderFilterPaymentMethodTypes } from '@/types/userOrders.interface'

const TransactionTypeFilter = ({
  value,
  onChange,
}: {
  value: CreditFilterDepositIdTypes[]
  onChange: (value: CreditFilterDepositIdTypes[]) => void
}) => (
  <CheckboxGroup value={value} onChange={onChange}>
    <VStack spacing={2} alignItems="flex-start" p={4}>
      {TRANSACTION_FILTER_CONFIG.map((config) => (
        <Checkbox
          key={config.id}
          value={config.id}
          size="sm"
          data-test-id={`filter-checkbox-${config.id}`}
        >
          {config.label}
        </Checkbox>
      ))}
    </VStack>
  </CheckboxGroup>
)

const PaymentTypeFilter = ({
  value,
  onChange,
  tab,
}: {
  value: OrderFilterPaymentMethodTypes[]
  onChange: (value: OrderFilterPaymentMethodTypes[]) => void
  tab: string | string[] | undefined
}) => {
  const configs = PAYMENT_TYPE_FILTER_CONFIG.filter(
    (c) => c.id !== 'ach' || tab === 'payments' || tab === 'invoices'
  )

  return (
    <CheckboxGroup value={value} onChange={onChange}>
      <VStack spacing={2} alignItems="flex-start" p={4}>
        {configs.map((config) => (
          <Checkbox
            key={config.id}
            value={config.id}
            size="sm"
            data-test-id={`filter-checkbox-${config.id}`}
          >
            {config.label}
          </Checkbox>
        ))}
      </VStack>
    </CheckboxGroup>
  )
}

const OrderTypeFilter = ({
  value,
  onChange,
}: {
  value: OrderFilterTypes[]
  onChange: (value: OrderFilterTypes[]) => void
}) => (
  <CheckboxGroup value={value} onChange={onChange}>
    <VStack spacing={2} alignItems="flex-start" p={4}>
      {ORDER_TYPE_FILTER_CONFIG.map((config) => (
        <Checkbox
          key={config.id}
          value={config.id}
          size="sm"
          data-test-id={`filter-checkbox-${config.id}`}
        >
          {config.label}
        </Checkbox>
      ))}
    </VStack>
  </CheckboxGroup>
)

const InvoiceTypeFilter = ({
  value,
  onChange,
}: {
  value: InvoiceTypes[] | null
  onChange: (value: InvoiceTypes[]) => void
}) => (
  <CheckboxGroup value={value || []} onChange={onChange}>
    <VStack spacing={2} alignItems="flex-start" p={4}>
      {UNPAID_INVOICE_FILTER_CONFIG.filter(
        (config) => config.filterConfig.type === 'invoiceType'
      ).map((type) => (
        <Checkbox
          key={type.id}
          value={type.id}
          size="sm"
          data-test-id={`filter-checkbox-${type.id}`}
        >
          {type.label}
        </Checkbox>
      ))}
    </VStack>
  </CheckboxGroup>
)

const HardwareInvoiceTypeFilter = ({
  value,
  onChange,
}: {
  value: HardwareInvoiceTypes[]
  onChange: (value: HardwareInvoiceTypes[]) => void
}) => (
  <CheckboxGroup value={value} onChange={onChange}>
    <VStack spacing={2} alignItems="flex-start" p={4}>
      {UNPAID_INVOICE_FILTER_CONFIG.filter(
        (config) => config.filterConfig.type === 'hardwareInvoiceType'
      ).map((type) => (
        <Checkbox
          key={type.id}
          value={type.id}
          size="sm"
          data-test-id={`filter-checkbox-${type.id}`}
        >
          {type.label}
        </Checkbox>
      ))}
    </VStack>
  </CheckboxGroup>
)

interface AmountFilterProps {
  value: { min: string; max: string }
  onChange: (value: { min: string; max: string }) => void
}

export const AmountFilter = ({ value, onChange }: AmountFilterProps) => (
  <VStack spacing={3} p={4}>
    <Box w="full">
      <Text fontSize="xs" color="gray.600" mb={1} ml={1}>
        Min
      </Text>
      <InputGroup size="sm">
        <InputLeftElement color="gray.500">$</InputLeftElement>
        <Input
          placeholder="0"
          type="number"
          max={1000000}
          value={value.min}
          onChange={(e) => onChange({ ...value, min: e.target.value })}
          data-test-id="filter-input-min"
        />
      </InputGroup>
    </Box>
    <Box w="full">
      <Text fontSize="xs" color="gray.600" mb={1} ml={1}>
        Max
      </Text>
      <InputGroup size="sm">
        <InputLeftElement color="gray.500">$</InputLeftElement>
        <Input
          placeholder="1000"
          type="number"
          max={1000000}
          value={value.max}
          onChange={(e) => onChange({ ...value, max: e.target.value })}
          data-test-id="filter-input-max"
        />
      </InputGroup>
    </Box>
  </VStack>
)

export const DateFilter = ({
  value,
  onChange,
}: {
  value: [Date | null, Date | null]
  onChange: (value: [Date | null, Date | null]) => void
}) => (
  <Box>
    <DateRangePicker
      placeholder="Select date range"
      defaultValue={value}
      handleChange={onChange}
      data-test-id="filter-datepicker"
    />
  </Box>
)

interface BillingFilterPanelProps {
  filters: ITransactionFilters
  setFilters: (filters: ITransactionFilters | null) => void
  subTab?: string
}

export const BillingFilterPanel = ({
  filters,
  setFilters,
  subTab,
}: BillingFilterPanelProps) => {
  const t = useTranslations()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const isMobile = useBreakpointValue({ base: true, md: false })
  const router = useRouter()
  const tab = router.query.tab

  const [localFilters, setLocalFilters] = useState<ITransactionFilters>(filters)

  useEffect(() => {
    setLocalFilters(filters)
  }, [filters])

  const bgColor = useColorModeValue('white', 'black.100')
  const borderColor = useColorModeValue('gray.100', 'gray.500')
  const bgGray50 = useColorModeValue('gray.50', 'black.200')
  const bgGray100 = useColorModeValue('gray.100', 'black.300')

  const handleApplyFilters = () => {
    setFilters(localFilters)

    onClose()
  }

  const handleClearFilters = () => {
    setFilters(null)
    onClose()
  }

  if (!filters) {
    return null
  }
  const isOrder = tab === 'orders'
  const isInvoice = subTab === 'invoices'

  const filterOptions = [
    {
      name: 'transaction-type',
      label: 'dashboard.billing.transaction-type',
      content: (
        <TransactionTypeFilter
          value={localFilters.selectedTypeIds}
          onChange={(v: CreditFilterDepositIdTypes[]) =>
            setLocalFilters((p) => ({ ...p, selectedTypeIds: v }))
          }
        />
      ),
      display: tab === 'credits',
    },
    {
      name: 'invoice-type',
      label: 'dashboard.billing.invoice-type',
      content: (
        <InvoiceTypeFilter
          value={localFilters.invoiceType}
          onChange={(v: InvoiceTypes[]) =>
            setLocalFilters((p) => ({ ...p, invoiceType: v }))
          }
        />
      ),
      display: tab === 'invoices' || isInvoice,
    },
    {
      name: 'invoice-subtype',
      label: 'dashboard.billing.invoice-subtype',
      content: (
        <HardwareInvoiceTypeFilter
          value={localFilters.hardwareInvoiceType}
          onChange={(v: HardwareInvoiceTypes[]) =>
            setLocalFilters((p) => ({ ...p, hardwareInvoiceType: v }))
          }
        />
      ),
      display: tab === 'invoices' || isInvoice,
    },
    {
      name: 'invoice-amount',
      label: 'dashboard.billing.invoice-amount',
      content: (
        <AmountFilter
          value={{ min: localFilters.minAmount, max: localFilters.maxAmount }}
          onChange={(v: { min: string; max: string }) =>
            setLocalFilters((p) => ({
              ...p,
              minAmount: v.min,
              maxAmount: v.max,
            }))
          }
        />
      ),
      display: tab === 'invoices' || isInvoice,
    },
    {
      name: 'wallet-amount',
      label: 'dashboard.compass-wallet.amount',
      content: (
        <AmountFilter
          value={{ min: localFilters.minAmount, max: localFilters.maxAmount }}
          onChange={(v: { min: string; max: string }) =>
            setLocalFilters((p) => ({
              ...p,
              minAmount: v.min,
              maxAmount: v.max,
            }))
          }
        />
      ),
      display: tab === 'credits',
    },
    {
      name: 'date',
      label: 'dashboard.compass-wallet.date',
      content: (
        <DateFilter
          value={localFilters.dateRange}
          onChange={(v: [Date | null, Date | null]) =>
            setLocalFilters((p) => ({ ...p, dateRange: v }))
          }
        />
      ),
      display: true,
    },
    {
      name: 'credits-applied',
      label: 'dashboard.billing.credits-applied',
      content: (
        <AmountFilter
          value={{ min: localFilters.minCredits, max: localFilters.maxCredits }}
          onChange={(v: { min: string; max: string }) =>
            setLocalFilters((p) => ({
              ...p,
              minCredits: v.min,
              maxCredits: v.max,
            }))
          }
        />
      ),
      display: isOrder || isInvoice,
    },
    {
      name: 'paid-amount',
      label: isOrder
        ? 'dashboard.billing.total-paid'
        : 'dashboard.billing.paid-amount',
      content: (
        <AmountFilter
          value={{ min: localFilters.minAmount, max: localFilters.maxAmount }}
          onChange={(v: { min: string; max: string }) =>
            setLocalFilters((p) => ({
              ...p,
              minAmount: v.min,
              maxAmount: v.max,
            }))
          }
        />
      ),
      display: isOrder || subTab === 'payments',
    },
    {
      name: 'included-fees',
      label: 'dashboard.billing.included-fees',
      content: (
        <AmountFilter
          value={{ min: localFilters.minAmount, max: localFilters.maxAmount }}
          onChange={(v: { min: string; max: string }) =>
            setLocalFilters((p) => ({
              ...p,
              minAmount: v.min,
              maxAmount: v.max,
            }))
          }
        />
      ),
      display: subTab === 'payments',
    },
    {
      name: 'payment-type',
      label:
        isOrder || isInvoice
          ? 'dashboard.billing.payment-type'
          : 'dashboard.billing.payment-method',
      content: (
        <PaymentTypeFilter
          value={localFilters.paymentMethod}
          onChange={(v: OrderFilterPaymentMethodTypes[]) =>
            setLocalFilters((p) => ({ ...p, paymentMethod: v }))
          }
          tab={subTab}
        />
      ),
      display: isOrder || subTab === 'payments' || isInvoice,
    },
    {
      name: 'order-type',
      label: 'dashboard.billing.order-type',
      content: (
        <OrderTypeFilter
          value={localFilters.orderType}
          onChange={(v: OrderFilterTypes[]) =>
            setLocalFilters((p) => ({ ...p, orderType: v }))
          }
        />
      ),
      display: isOrder,
    },
  ]

  const handleApply = (onClose?: () => void) => {
    handleApplyFilters()
    onClose && onClose()
  }
  const handleClear = (onClose?: () => void) => {
    handleClearFilters()
    onClose && onClose()
  }

  const renderFilters = (onClose?: () => void) => (
    <>
      <Accordion allowMultiple defaultIndex={[0]} w="full">
        {filterOptions.map((option, index) => (
          <AccordionItem
            key={index}
            borderTopWidth={index === 0 ? '0px' : '1px'}
            display={option.display ? 'inherit' : 'none'}
          >
            <AccordionButton
              _hover={{ bg: bgGray100 }}
              bg={bgGray50}
              py={3}
              px={4}
              data-test-id={`filter-category-${option.name}`}
            >
              <Box
                flex="1"
                textAlign="left"
                fontWeight="semibold"
                fontSize="sm"
              >
                {t(option.label)}
              </Box>
              <AccordionIcon />
            </AccordionButton>

            <AccordionPanel p={0}>{option.content}</AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
      <HStack
        justifyContent="space-between"
        p={4}
        borderTopWidth="1px"
        borderColor="gray.200"
      >
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleClear(onClose)}
          w="50%"
          data-test-id="filter-clear"
        >
          {t('dashboard.miners.filter.clear')}
        </Button>
        <Button
          bg="gray.800"
          color="white"
          _hover={{ bg: 'gray.700' }}
          size="sm"
          onClick={() => handleApply(onClose)}
          w="50%"
          data-test-id="filter-apply"
        >
          {t('checkout.offers.apply')}
        </Button>
      </HStack>
    </>
  )

  if (isMobile) {
    return (
      <>
        <Button
          justifyContent={'center'}
          alignItems={'center'}
          height="32px"
          padding="10px 12px"
          gap="8px"
          variant="outline"
          iconSpacing={0}
          leftIcon={<Icon as={FiltersIcon} />}
          onClick={onOpen}
          data-test-id="filter-button"
        />
        <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
          <DrawerOverlay />
          <DrawerContent borderTopRadius="lg">
            <DrawerHeader
              borderBottomWidth="1px"
              fontSize="lg"
              fontWeight="semibold"
            >
              <Icon as={FiltersIcon} mr={2} />
              {t('hardware-list.labels.filter')}
            </DrawerHeader>
            <DrawerCloseButton />
            <DrawerBody p={0} overflowY="auto" maxH="60vh">
              {renderFilters(onClose)}
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </>
    )
  }

  return (
    <Box position="relative" zIndex={1000}>
      <Popover
        placement="bottom-end"
        closeOnBlur={true}
        isLazy
        strategy="fixed"
      >
        {({ onClose }) => (
          <>
            <PopoverTrigger>
              <Button
                justifyContent={'center'}
                alignItems={'center'}
                height="32px"
                padding="10px 12px"
                gap="8px"
                variant="outline"
                iconSpacing={0}
                leftIcon={<Icon as={FiltersIcon} />}
                data-test-id="filter-button"
              />
            </PopoverTrigger>
            <PopoverContent
              width={{ base: '90vw', sm: '320px' }}
              boxShadow="lg"
              borderRadius="md"
              zIndex={1000}
              borderWidth="1px"
              borderColor={borderColor}
              bg={bgColor}
              overflowY={'auto'}
              maxH={'60vh'}
              sx={{
                // Scrollbar styling
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'gray.300',
                  borderRadius: '10px',
                  border: '2px solid transparent',
                  backgroundClip: 'content-box',
                },
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: 'gray.400',
                  border: '2px solid transparent',
                  backgroundClip: 'content-box',
                  cursor: 'pointer',
                },
              }}
            >
              <PopoverArrow />

              <PopoverBody p={3} mt="3">
                {renderFilters(onClose)}
              </PopoverBody>
            </PopoverContent>
          </>
        )}
      </Popover>
    </Box>
  )
}
